import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { IoService } from '../../../services/io/io.service';
import { SupportService } from '../../../services/support/support.service';
import { SettingsService } from '../../../services/settings/settings.service';
import { LoadingComponent } from '../../shared/loading/loading.component';
import { AppComponent } from '../../../app.component';
import { DialogOption } from '../../shared/ui/dialog/dialog-options';

@Component({
  selector: 'app-report-editor',
  templateUrl: './report-editor.component.html',
  styleUrls: ['./report-editor.component.scss']
})
export class ReportEditorComponent implements OnInit {

  reportId: string;
  isNew = false;
  report = null;
  saving = false;

  reportTypes = [
    { key: '', name: 'standard' },
    { key: 'custom', name: 'custom' },
    { key: 'specialty', name: 'specialty' },
    { key: 'total', name: 'total' },
    { key: 'document', name: 'document' }
  ];

  printTemplateKeysText = '';
  searchPathsText = '';
  templateKeysText = '';
  contextTypeText = '';
  newActiveFieldText = '';

  supportActive = false;
  settingsActive = false;

  constructor(
    private ioService: IoService,
    private supportService: SupportService,
    private settingsService: SettingsService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.supportService.supportActive$.subscribe((active) => {
      this.supportActive = active;
    });
    this.supportActive = this.supportService.isSupportActive();

    this.settingsService.settingsActive$.subscribe((active) => {
      this.settingsActive = active;
    });
    this.settingsActive = this.settingsService.isSettingsActive();

    this.route.params.subscribe(params => {
      this.reportId = params.documentId;
      this.isNew = this.reportId === 'new';

      if (this.isNew) {
        this.setReport({
          name: '',
          key: '',
          active: true,
          collection: '',
          fields: [],
          activeFields: [],
          filters: [],
          sorts: []
        });
      } else {
        this.loadReport();
      }
    });
  }

  loadReport() {
    LoadingComponent.showLoading();
    this.ioService.post('/reportEditor/getReport', {
      reportId: this.reportId
    }).then((report: any) => {
      this.setReport(report);
      LoadingComponent.hideLoading();
    }).catch((err: any) => {
      LoadingComponent.hideLoading();
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to load report.', 'OK', 5000);
      this.router.navigate(['/editor/reports']);
    });
  }

  setReport(report) {
    report.fields = report.fields || [];
    report.activeFields = report.activeFields || [];
    // Filters are a typed array on report templates. Self-heal the legacy/corrupted
    // numeric-keyed object form so it is never written back as an object.
    if (Array.isArray(report.filters)) {
      // keep as-is
    } else if (report.filters && typeof report.filters === 'object') {
      report.filters = Object.keys(report.filters)
        .sort((a, b) => Number(a) - Number(b))
        .map(key => report.filters[key]);
    } else {
      report.filters = [];
    }
    report.sorts = report.sorts || [];
    report.charts = Array.isArray(report.charts) ? report.charts.map(chart => this.normalizeChart(chart)) : [];
    report.type = report.type || '';

    this.report = report;

    this.printTemplateKeysText = this.listToText(report.printTemplateKeys);
    this.templateKeysText = this.listToText(report.templateKeys);
    this.contextTypeText = this.listToText(report.contextType);
    this.searchPathsText = this.listToText(report.searchPaths);
  }

  // Report.searchPaths: always-searched document paths (comma-separated in the editor)
  searchPathsChanged(text: string) {
    this.searchPathsText = text;
  }

  listToText(value): string {
    if (!value) {
      return '';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  }

  textToArray(text: string) {
    const items = text.split(',').map(item => item.trim()).filter(item => item !== '');
    return items.length > 0 ? items : null;
  }

  // ----- Fields -----

  addField() {
    this.report.fields.push({
      path: '',
      name: '',
      type: 'string'
    });
  }

  removeField(index: number) {
    const removed = this.report.fields.splice(index, 1)[0];
    if (removed && removed.path) {
      this.report.activeFields = this.report.activeFields.filter(path => path !== removed.path);
    }
  }

  moveField(index: number, offset: number) {
    const target = index + offset;
    if (target < 0 || target >= this.report.fields.length) {
      return;
    }
    const field = this.report.fields.splice(index, 1)[0];
    this.report.fields.splice(target, 0, field);
  }

  // ----- Charts -----

  normalizeChart(chart: any) {
    return {
      type: 'line',
      xBucket: 'day',
      aggregate: 'sum',
      seriesPath: '',
      ...chart,
      yPaths: Array.isArray(chart.yPaths) ? chart.yPaths : [],
    };
  }

  addChart() {
    this.report.charts.push(this.normalizeChart({ name: '', key: '', xPath: this.report.datePath || '' }));
  }

  removeChart(index: number) {
    this.report.charts.splice(index, 1);
  }

  moveChart(index: number, offset: number) {
    const target = index + offset;
    if (target < 0 || target >= this.report.charts.length) {
      return;
    }
    const chart = this.report.charts.splice(index, 1)[0];
    this.report.charts.splice(target, 0, chart);
  }

  // ----- Active Fields -----

  isActiveField(path: string): boolean {
    return this.report.activeFields.indexOf(path) !== -1;
  }

  toggleActiveField(path: string, checked: boolean) {
    if (checked && !this.isActiveField(path)) {
      this.report.activeFields.push(path);
    } else if (!checked) {
      this.report.activeFields = this.report.activeFields.filter(activePath => activePath !== path);
    }
  }

  get orphanedActiveFields() {
    const fieldPaths = this.report.fields.map(field => field.path);
    return this.report.activeFields.filter(path => fieldPaths.indexOf(path) === -1);
  }

  addActiveField() {
    const path = this.newActiveFieldText.trim();
    if (path && !this.isActiveField(path)) {
      this.report.activeFields.push(path);
    }
    this.newActiveFieldText = '';
  }

  // ----- Sorts -----

  addSort() {
    this.report.sorts.push({ path: '', direction: 1 });
  }

  removeSort(index: number) {
    this.report.sorts.splice(index, 1);
  }

  // ----- Computed Fields -----

  addComputedField() {
    if (!this.report.computedFields) {
      this.report.computedFields = [];
    }
    this.report.computedFields.push({ action: 'divide', path: '', inputs: ['', ''] });
  }

  removeComputedField(index: number) {
    this.report.computedFields.splice(index, 1);
  }

  // ----- Save / Delete -----

  buildPayload() {
    const payload: any = {};

    const textFields = [
      'key', 'name', 'description', 'category', 'subtype', 'collection',
      'locationPath', 'datePath', 'groupPath', 'recordsPath', 'searchIndex',
      'itemType', 'viewRoute', 'addRoute', 'addPermission',
      'importRoute', 'importPermission', 'importType', 'defaultTemplateKey', 'defaultDateRange'
    ];

    for (const key of textFields) {
      payload[key] = this.report[key] ? this.report[key] : null;
    }

    // Never unset the identifying fields; leave them as-is when cleared
    if (!payload.key) {
      delete payload.key;
    }
    if (!payload.name) {
      delete payload.name;
    }

    payload.type = this.report.type ? this.report.type : null;

    const booleanFields = ['unlisted', 'default', 'secondarySort', 'supportsIncrementalLoading', 'secondaryReadPreference'];
    for (const key of booleanFields) {
      payload[key] = this.report[key] ? true : null;
    }

    payload.active = this.report.active !== false;

    const chunkSize = parseFloat(this.report.chunkSize);
    payload.chunkSize = isNaN(chunkSize) ? null : chunkSize;

    payload.fields = this.report.fields;
    payload.activeFields = this.report.activeFields;
    payload.filters = Array.isArray(this.report.filters) && this.report.filters.length > 0 ? this.report.filters : null;
    payload.sorts = this.report.sorts.filter(sort => sort.path);

    const computedFields = (this.report.computedFields || [])
      .filter(computed => computed.path && computed.inputs[0] && computed.inputs[1]);
    payload.computedFields = computedFields.length > 0 ? computedFields : null;

    payload.printTemplateKeys = this.textToArray(this.printTemplateKeysText);
    payload.templateKeys = this.textToArray(this.templateKeysText);
    payload.searchPaths = this.textToArray(this.searchPathsText);

    const contextTypes = this.textToArray(this.contextTypeText);
    payload.contextType = contextTypes === null ? null : (contextTypes.length === 1 ? contextTypes[0] : contextTypes);

    // Charts: drop incomplete rows, omit empty options, unset when none remain.
    const charts = (this.report.charts || [])
      .filter(chart => chart.name && chart.key && chart.xPath)
      .map(chart => {
        const serialized: any = { key: chart.key, name: chart.name, xPath: chart.xPath, yPaths: chart.yPaths || [] };
        if (chart.type && chart.type !== 'line') { serialized.type = chart.type; }
        if (chart.xBucket && chart.xBucket !== 'day') { serialized.xBucket = chart.xBucket; }
        if (chart.seriesPath) { serialized.seriesPath = chart.seriesPath; }
        if (chart.aggregate && chart.aggregate !== 'sum') { serialized.aggregate = chart.aggregate; }
        if (chart.dashboard) { serialized.dashboard = chart.dashboard; }
        return serialized;
      });
    payload.charts = charts.length > 0 ? charts : null;

    payload.addData = this.report.addData && Object.keys(this.report.addData).length > 0 ? this.report.addData : null;
    payload.settings = this.report.settings && Object.keys(this.report.settings).length > 0 ? this.report.settings : null;

    return payload;
  }

  async save() {
    if (!this.report.name) {
      AppComponent.showSnack('Report name is required.', 'OK', 5000);
      return;
    }

    if (this.saving) {
      return;
    }

    this.saving = true;
    LoadingComponent.showLoading();

    try {
      if (this.isNew) {
        const created: any = await this.ioService.post('/reportEditor/createReport', {
          report: this.buildPayload()
        });
        AppComponent.showSnack('Report created.', 'OK', 3000);
        this.router.navigate(['/editor/reports', created._id], { replaceUrl: true });
      } else {
        const updated: any = await this.ioService.post('/reportEditor/updateReport', {
          reportId: this.reportId,
          changes: this.buildPayload()
        });
        this.setReport(updated);
        AppComponent.showSnack('Report saved.', 'OK', 3000);
      }
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to save report.', 'OK', 5000);
    }

    this.saving = false;
    LoadingComponent.hideLoading();
  }

  deactivate() {
    const dialogRef = AppComponent.showMessage(
      'Deactivate Report',
      `Deactivate the report "${this.report.name}"? It will no longer be available to users.`,
      ['Confirm', 'Cancel'],
      false
    );

    dialogRef.afterClosed().subscribe(async (result: DialogOption) => {
      if (!result || result.value !== 'Confirm') {
        return;
      }

      try {
        await this.ioService.post('/reportEditor/deleteReport', {
          reportId: this.reportId
        });
        AppComponent.showSnack('Report deactivated.', 'OK', 3000);
        this.router.navigate(['/editor/reports']);
      } catch (err) {
        AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to deactivate report.', 'OK', 5000);
      }
    });
  }

  back() {
    this.router.navigate(['/editor/reports']);
  }
}
